"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Bell } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { cn } from "@/lib/utils";
import { NotificationPanel } from "./NotificationPanel";

/**
 * Header trigger for the notification inbox. Renders {@link Bell} or
 * {@link BellDot} based on whether there are unseen notifications. Opens
 * a Radix {@link Popover} that hosts {@link NotificationPanel}.
 *
 * <p>When the panel opens the visible items are flagged "seen" via
 * {@code markSeenForVisible(visibleIds)} (N parallel single-item PATCHes
 * — no bulk endpoint). Reading happens separately on click.
 */
export function NotificationBell(t0) {
  const $ = _c(23);
  const {
    popoverSide: t1,
    align: t2,
    className
  } = t0;
  const popoverSide = t1 === undefined ? "bottom" : t1;
  const align = t2 === undefined ? "end" : t2;
  const [open, setOpen] = useState(false);
  const unseenCount = useNotificationStore(_temp);
  const markSeenForVisible = useNotificationStore(_temp2);
  const notifications = useNotificationStore(_temp3);
  let t3;
  if ($[0] !== markSeenForVisible || $[1] !== notifications) {
    t3 = function onOpenChange(next) {
      setOpen(next);
      if (next) {
        const visibleIds = notifications.slice(0, 50).map(_temp4);
        markSeenForVisible(visibleIds);
      }
    };
    $[0] = markSeenForVisible;
    $[1] = notifications;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const onOpenChange = t3;
  let t4;
  if ($[3] !== className) {
    t4 = cn("relative h-9 w-9 shrink-0", className);
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = unseenCount > 0 ? `${unseenCount} unseen notifications` : "Notifications";
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Bell className="h-5 w-5" aria-hidden={true} />;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== unseenCount) {
    t7 = unseenCount > 0 && <motion.span key={unseenCount} initial={{
      scale: 0.5,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 1
    }} exit={{
      scale: 0.5,
      opacity: 0
    }} transition={{
      duration: 0.15
    }} className="absolute -right-1 -top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none tabular-nums text-primary-foreground shadow">{unseenCount > 99 ? "99+" : unseenCount}</motion.span>;
    $[6] = unseenCount;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t7) {
    t8 = <AnimatePresence>{t7}</AnimatePresence>;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t8) {
    t9 = <PopoverTrigger asChild={true}><Button type="button" variant="ghost" size="icon" className={t4} aria-label={t5}>{t6}{t8}</Button></PopoverTrigger>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <NotificationPanel onItemSelected={() => setOpen(false)} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== align || $[16] !== popoverSide) {
    t11 = <PopoverContent align={align} side={popoverSide} sideOffset={8} className="w-[380px] p-0 max-h-[70vh] overflow-hidden flex flex-col">{t10}</PopoverContent>;
    $[15] = align;
    $[16] = popoverSide;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== onOpenChange || $[19] !== open || $[20] !== t11 || $[21] !== t9) {
    t12 = <Popover open={open} onOpenChange={onOpenChange}>{t9}{t11}</Popover>;
    $[18] = onOpenChange;
    $[19] = open;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
function _temp4(n) {
  return n.id;
}
function _temp3(s_1) {
  return s_1.notifications;
}
function _temp2(s_0) {
  return s_0.markSeenForVisible;
}
function _temp(s) {
  return s.unseenCount;
}